import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import {
  handleErrorToast,
  handleSuccessToast,
  handleWarningToast,
} from 'src/components/molecules';
import {
  CategoryType,
  GetOrganisationCategoriesQuery,
  useCreateCategoryMutation,
  useGetOrganisationCategoriesQuery,
  useUpdateCategoryLocaleMutation,
  useUpdateCategoryMutation,
  useUpdateCategoryOrdersMutation,
} from 'src/graphql';
import { FeatureFlag } from 'src/types';
import { useIsFeatureEnabled } from '../useIsFeatureEnabled';
import { getDisabledIntakeMethods } from './helpers';

const NEW_CATEGORY = {
  allowGoTogether: false,
  displayInApp: false,
  displayInOpenReporting: false,
  displayInVaultTalk: false,
  displayInManualIntake: false,
  displayInCollaborationToolIntegration: false,
  isDeterminedCategory: false,
  isReportedCategory: false,
  locales: {},
  order: 0,
  question: false,
  requestOpenPerpetrators: false,
};

interface Parameters {
  setNetworkError: (networkError: boolean) => void;
  setOpenIds: Dispatch<SetStateAction<number[]>>;
}

export const useCategories = ({ setNetworkError, setOpenIds }: Parameters) => {
  const queryClient = useQueryClient();
  const [currentCategory, setCurrentCategory] = useState<CategoryType | null>(
    null,
  );
  const [newCategory, setNewCategory] = useState<number | null>(null);
  const newCategoryRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, refetch } = useGetOrganisationCategoriesQuery({
    isCOI: false,
  });

  const categories = useMemo(() => {
    return (data?.getOrganisationCategories as CategoryType[]) ?? [];
  }, [data]);

  const {
    data: coiData,
    isLoading: isCoiLoading,
    refetch: refetchCoi,
  } = useGetOrganisationCategoriesQuery({
    isCOI: true,
  });

  const coiCategories = useMemo(() => {
    return (coiData?.getOrganisationCategories as CategoryType[]) ?? [];
  }, [coiData]);

  const fetchCategories = async () => {
    const [speakUpResult, coiResult] = await Promise.allSettled([
      refetch(),
      refetchCoi(),
    ]);

    if (
      speakUpResult.status === 'rejected' ||
      coiResult.status === 'rejected'
    ) {
      setNetworkError(true);
      handleErrorToast({
        toastMessage: 'Error refreshing categories',
        toastDescription: 'Please refresh the page and try again.',
      });
    }
  };

  const { mutate: update } = useUpdateCategoryMutation({
    onSuccess: async () => {
      queryClient.refetchQueries({ queryKey: ['GetCategoriesAuditEntries'] });
      handleSuccessToast({ toastMessage: 'Category updated' });
      fetchCategories();
    },
    onError: (error: unknown) => {
      handleErrorToast({
        toastMessage: 'Error updating category',
        toastDescription: error instanceof Error ? error.message : undefined,
      });
    },
  });
  const { mutate: updateLocale } = useUpdateCategoryLocaleMutation({
    onSuccess: async () => {
      queryClient.refetchQueries({
        queryKey: ['GetCategoriesAuditEntries'],
      });
      handleSuccessToast({ toastMessage: 'Category updated' });
      fetchCategories();
    },
    onError: () => setNetworkError(true),
  });
  const { mutate: create } = useCreateCategoryMutation({
    onMutate: () => {
      setOpenIds([]);
    },
    onSuccess: (data) => {
      queryClient.refetchQueries({ queryKey: ['GetCategoriesAuditEntries'] });
      handleSuccessToast({ toastMessage: 'Category created' });
      setOpenIds([data?.createCategory?.id ?? 0]);
      setNewCategory(data?.createCategory?.id ?? null);
      fetchCategories();
    },
  });

  const { mutate: updateOrder } = useUpdateCategoryOrdersMutation({
    onMutate: async (variables) => {
      if (Array.isArray(variables.categories)) {
        queryClient.cancelQueries(['GetOrganisationCategories']);

        const draggedCategory = variables.draggedCategoryId;
        const isCOICategory =
          coiCategories.some((cat) => cat.id === draggedCategory) ||
          (currentCategory?.id === draggedCategory && currentCategory.isCOI);

        const queryKey = [
          'GetOrganisationCategories',
          { isCOI: isCOICategory },
        ];
        const existingData = queryClient.getQueryData(
          queryKey,
        ) as GetOrganisationCategoriesQuery;

        if (!existingData?.getOrganisationCategories) {
          return;
        }

        const existingCategories = existingData.getOrganisationCategories;

        const newOrderedCategories = variables.categories.map((category) => {
          const completeCategory = existingCategories?.find(
            (existingCategory) => String(existingCategory.id) === category.id,
          );
          return { ...completeCategory, order: category.order };
        });

        queryClient.setQueryData(queryKey, {
          getOrganisationCategories: newOrderedCategories,
        });
      }
    },

    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GetCategoriesAuditEntries'] });
      fetchCategories();
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Error updating category order' });
      fetchCategories();
    },
  });

  const isOPREnabled = useIsFeatureEnabled(FeatureFlag.PUBLIC_REPORTING);
  const isVTEnabled = useIsFeatureEnabled(FeatureFlag.VAULT_TALK);
  const isDynamicQuestionsV1Enabled = useIsFeatureEnabled(
    FeatureFlag.DYNAMIC_QUESTIONS_V1,
  );

  useEffect(() => {
    if (categories.length > 0) {
      setCurrentCategory(categories[categories.length - 1]);
    }
    // do not want to trigger this when categories are edited, only when a new category is added
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length]);

  useEffect(() => {
    if (categories.length) {
      if (isDynamicQuestionsV1Enabled && (isOPREnabled || isVTEnabled)) {
        return;
      }
      const allCategoriesOff = categories.every(
        (category) => !category.isReportedCategory,
      );
      const disabledIntakeMethods = getDisabledIntakeMethods(
        categories,
        isOPREnabled,
        isVTEnabled,
      );

      if (allCategoriesOff || disabledIntakeMethods.length > 0) {
        let disabledIntakeMethodsMessage = '';
        let acrossIntakeMethodsMessage = 'across intake methods';
        if (!allCategoriesOff && disabledIntakeMethods.length === 1) {
          disabledIntakeMethodsMessage = ` for ${disabledIntakeMethods[0]}`;
          acrossIntakeMethodsMessage = 'for this intake method';
        }
        if (!allCategoriesOff && disabledIntakeMethods.length > 1) {
          disabledIntakeMethodsMessage = ` for ${disabledIntakeMethods
            .slice(0, -1)
            .join(', ')} and ${disabledIntakeMethods.at(-1)}`;
          acrossIntakeMethodsMessage = 'for these intake methods';
        }
        handleWarningToast({
          toastMessage: 'No categories are enabled across intake methods.',
          toastDescription: `This means no reports can be submitted${disabledIntakeMethodsMessage}. Please ensure at least one category is enabled ${acrossIntakeMethodsMessage}.`,
        });
      }
    }
  }, [categories, isOPREnabled, isVTEnabled, isDynamicQuestionsV1Enabled]);

  useEffect(() => {
    if (!newCategory) return;
    const el = newCategoryRef.current;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setNewCategory(null);
    }
  }, [newCategory, categories]);

  const updateCategory = () => {
    if (currentCategory) {
      const {
        createdAt: _createdAt,
        Author: _author,
        status: _status,
        hasActiveLinkedForm: _hasActiveLinkedForm,
        ...categoryForUpdating
      } = currentCategory;
      update({
        category: categoryForUpdating,
      });
    }
  };

  const updateCategoryLocale = (
    id: number,
    locale: string,
    name: string,
    description: string,
  ) => {
    const category = { id, locale, name, description };
    updateLocale({ category });
  };

  const createCategory = ({ isCOI }: { isCOI: boolean }) => {
    const lastCategory = isCOI ? coiCategories.at(-1) : categories.at(-1);
    create({
      category: {
        ...NEW_CATEGORY,
        order: lastCategory ? lastCategory.order + 1 : 0,
        isCOI,
      },
    });
  };

  return {
    currentCategory,
    setCurrentCategory,
    categories,
    coiCategories,
    isLoading,
    isCoiLoading,
    updateCategory,
    updateCategoryLocale,
    updateOrder,
    createCategory,
    newCategory,
    newCategoryRef,
  };
};
